/**
 * Hungarian Translations — Impact Core i18n
 *
 * Flat key-value structure. Keys use dot notation for namespacing.
 * Coverage: navigation, actions, statuses, dashboard labels, form labels.
 */

export const hu: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Vezérlőpult',
  'nav.funds': 'Alapok',
  'nav.investments': 'Befektetések',
  'nav.investees': 'Befektetési célpontok',
  'nav.esg': 'ESG Felülvizsgálat',
  'nav.compliance': 'Megfelelőség',
  'nav.reports': 'Jelentések',
  'nav.settings': 'Beállítások',
  'nav.users': 'Felhasználók',
  'nav.sfdr': 'SFDR',
  'nav.pai': 'PAI Mutatók',
  'nav.taxonomy': 'EU Taxonómia',
  'nav.filings': 'Bejelentések',
  'nav.calendar': 'Naptár',
  'nav.submit': 'Adatok benyújtása',
  'nav.submissions': 'Benyújtásaim',
  'nav.benchmarks': 'Referenciaértékek',
  'nav.messages': 'Üzenetek',
  'nav.portfolio': 'Portfólió',
  'nav.performance': 'Alap teljesítmény',
  'nav.esgSummary': 'ESG Összefoglaló',
  'nav.pipeline': 'Csővezeték',
  'nav.dueDiligence': 'Átvilágítás',
  'nav.impact': 'Hatásmutatók',
  'nav.sdg': 'FFT Feltérképezés',
  'nav.audit': 'Ellenőrzési nyomvonal',
  'nav.snapshots': 'Adatpillanatképek',
  'nav.knowledgeGraph': 'Tudásgráf',
  'nav.geographic': 'Földrajzi',
  'nav.bi': 'BI Stúdió',
  'nav.ai': 'AI Asszisztens',
  'nav.tasks': 'Feladatok',
  'nav.csrd': 'CSRD',
  'nav.regulatory': 'Szabályozási monitor',

  // Common Actions
  'action.create': 'Létrehozás',
  'action.edit': 'Szerkesztés',
  'action.delete': 'Törlés',
  'action.save': 'Mentés',
  'action.cancel': 'Mégsem',
  'action.submit': 'Benyújtás',
  'action.approve': 'Jóváhagyás',
  'action.reject': 'Elutasítás',
  'action.download': 'Letöltés',
  'action.export': 'Exportálás',
  'action.import': 'Importálás',
  'action.filter': 'Szűrés',
  'action.search': 'Keresés',
  'action.refresh': 'Frissítés',
  'action.close': 'Bezárás',
  'action.back': 'Vissza',
  'action.next': 'Következő',
  'action.previous': 'Előző',
  'action.viewAll': 'Összes megtekintése',
  'action.viewDetails': 'Részletek megtekintése',
  'action.generate': 'Generálás',
  'action.signOut': 'Kijelentkezés',
  'action.addNew': 'Új hozzáadása',
  'action.invite': 'Meghívás',

  // Statuses
  'status.draft': 'Piszkozat',
  'status.submitted': 'Benyújtva',
  'status.validated': 'Érvényesítve',
  'status.approved': 'Jóváhagyva',
  'status.rejected': 'Elutasítva',
  'status.pending': 'Függőben',
  'status.inProgress': 'Folyamatban',
  'status.overdue': 'Késedelmes',
  'status.compliant': 'Megfelelő',
  'status.filed': 'Bejelentve',
  'status.active': 'Aktív',
  'status.inactive': 'Inaktív',
  'status.closed': 'Lezárt',
  'status.fundraising': 'Tőkegyűjtés',
  'status.investing': 'Befektetés',
  'status.harvesting': 'Betakarítás',
  'status.validating': 'Érvényesítés',
  'status.review': 'Felülvizsgálat',
  'status.monitoring': 'Megfigyelés',
  'status.exiting': 'Kilépés',
  'status.pipeline': 'Pipeline',
  'status.due_diligence': 'Átvilágítás',
  'status.disbursed': 'Kifizetett',
  'status.restructured': 'Átstrukturált',
  'status.matured': 'Lejárt',
  'status.written_off': 'Leírt',
  'status.exited': 'Kilépett',
  'status.incomplete': 'Hiányos',
  'status.open': 'Nyitott',
  'status.in_progress': 'Folyamatban',
  'status.completed': 'Befejezett',
  'status.cancelled': 'Visszavont',
  'status.accepted': 'Elfogadott',
  'status.failed': 'Sikertelen',

  // Dashboard Labels
  'dashboard.portfolioOverview': 'Portfólió áttekintés',
  'dashboard.complianceStatus': 'Megfelelőségi állapot',
  'dashboard.collectionProgress': 'Gyűjtés előrehaladása',
  'dashboard.activityFeed': 'Tevékenység',
  'dashboard.quickActions': 'Gyorsműveletek',
  'dashboard.investmentPipeline': 'Befektetési csővezeték',
  'dashboard.totalFunds': 'Összes alap',
  'dashboard.aum': 'AUM',
  'dashboard.activeInvestments': 'Aktív befektetések',
  'dashboard.totalInvestees': 'Befektetési célpontok',
  'dashboard.recentActivity': 'Legutóbbi tevékenység',

  // LP Dashboard Labels
  'lp.totalCommitted': 'Összes kötelezettségvállalás',
  'lp.capitalCalled': 'Lehívott tőke',
  'lp.distributed': 'Kiosztott',
  'lp.netPosition': 'Nettó pozíció',
  'lp.tvpi': 'TVPI',
  'lp.dpi': 'DPI',
  'lp.fundPerformance': 'Alap teljesítmény',
  'lp.esgSummary': 'ESG Összefoglaló',
  'lp.reportLibrary': 'Jelentéskönyvtár',
  'lp.taxonomyAligned': 'Taxonómiához igazított',
  'lp.paiCoverage': 'PAI Lefedettség',
  'lp.portfolioPerformance': 'LP Portfólió teljesítmény',

  // Fund Labels
  'fund.name': 'Alap neve',
  'fund.code': 'Alap kódja',
  'fund.vintage': 'Alapítás éve',
  'fund.size': 'Alap mérete',
  'fund.targetSize': 'Célméret',
  'fund.currency': 'Pénznem',
  'fund.strategy': 'Stratégia',
  'fund.status': 'Állapot',
  'fund.sfdrCategory': 'SFDR Kategória',
  'fund.geographyFocus': 'Földrajzi fókusz',
  'fund.sectorFocus': 'Szektorfókusz',

  // Form Labels
  'form.name': 'Név',
  'form.email': 'E-mail',
  'form.country': 'Ország',
  'form.amount': 'Összeg',
  'form.currency': 'Pénznem',
  'form.date': 'Dátum',
  'form.description': 'Leírás',
  'form.notes': 'Megjegyzések',
  'form.type': 'Típus',
  'form.period': 'Időszak',
  'form.year': 'Év',
  'form.required': 'Kötelező',
  'form.optional': 'Opcionális',

  // Table Headers
  'table.fund': 'Alap',
  'table.commitment': 'Kötelezettségvállalás',
  'table.called': 'Lehívott',
  'table.distributed': 'Kiosztott',
  'table.tvpi': 'TVPI',
  'table.status': 'Állapot',
  'table.regulation': 'Szabályozás',
  'table.filingType': 'Bejelentés típusa',
  'table.reportingPeriod': 'Időszak',
  'table.createdAt': 'Létrehozva',
  'table.actions': 'Műveletek',

  // Compliance
  'compliance.sfdr': 'SFDR',
  'compliance.taxonomy': 'EU Taxonómia',
  'compliance.pai': 'PAI',
  'compliance.dataCompleteness': 'Adatteljessség',
  'compliance.nextDeadline': 'Következő határidő',

  // Reports
  'report.quarterly': 'Negyedéves jelentés',
  'report.annual': 'Éves jelentés',
  'report.paiStatement': 'PAI Nyilatkozat',
  'report.taxonomyReport': 'Taxonómia jelentés',
  'report.generatedAt': 'Generálva',
  'report.version': 'Verzió',

  // General
  'general.loading': 'Betöltés...',
  'general.noData': 'Nincs elérhető adat',
  'general.error': 'Hiba történt',
  'general.retry': 'Újrapróbálás',
  'general.total': 'Összesen',
  'general.page': 'Oldal',
  'general.of': '/',
  'general.showing': 'Megjelenítve',
  'general.results': 'eredmény',
  'general.language': 'Nyelv',
  'general.english': 'Angol',
  'general.french': 'Francia',

  // Tour
  'tour.takeTour': 'Körúnézés indítása',
  'tour.skip': 'Kihagyás',
  'tour.next': 'Következő',
  'tour.prev': 'Előző',
  'tour.finish': 'Befejezés',


  // Tour Content, Form Labels, Table Headers, Actions, Errors (i18n wiring)
  'tour.sidebarTitle': 'Navigáció',
  'tour.sidebarContent': 'Használja az oldalsávot a portfóliókezelés, ESG adatgyűjtés, megfelelőség, jelentések és elemzőeszközök közötti navigáláshoz.',
  'tour.kpisTitle': 'Portfólió áttekintés',
  'tour.kpisContent': 'Ezek a kártyák egy pillantásra mutatják portfólióját — összes alap, AUM, aktív befektetések és befektetett vállalatok száma.',
  'tour.actionsTitle': 'Gyors műveletek',
  'tour.actionsContent': 'Hozzon létre alapokat, adjon hozzá befektetéseket, hívjon meg vállalatokat és készítsen jelentéseket egyetlen kattintással.',
  'tour.languageTitle': 'Nyelv',
  'tour.languageContent': 'Váltson 24 hivatalos EU-nyelv között. A teljes felület azonnal lefordítódik.',
  'tour.aiTitle': 'AI Asszisztens',
  'tour.aiContent': 'Tegyen fel kérdéseket portfóliójáról bármikor. Az AI asszisztens teljes kontextussal rendelkezik alapjairól és befektetéseiről.',
  'action.createFund': 'Alap létrehozása',
  'action.editFund': 'Alap szerkesztése',
  'action.addInvestment': 'Befektetés hozzáadása',
  'action.addInvestee': 'Vállalat hozzáadása',
  'action.editInvestee': 'Vállalat szerkesztése',
  'action.createTemplate': 'Sablon létrehozása',
  'action.createSubmission': 'Beküldés létrehozása',
  'action.createFiling': 'Beadvány létrehozása',
  'action.generateReport': 'Jelentés generálása',
  'action.inviteUser': 'Felhasználó meghívása',
  'action.sendInvitation': 'Meghívó küldése',
  'action.saveChanges': 'Módosítások mentése',
  'form.fundName': 'Alap neve',
  'form.fundCode': 'Alap kódja',
  'form.sfdrCategory': 'SFDR Kategória',
  'form.baseCurrency': 'Alapvaluta',
  'form.targetSize': 'Célméret',
  'form.vintageYear': 'Alapítás éve',
  'form.geographyFocus': 'Földrajzi fókusz',
  'form.sectorFocus': 'Ágazati fókusz',
  'form.investmentCode': 'Befektetési kód',
  'form.instrumentType': 'Eszköztípus',
  'form.maturityDate': 'Lejárati dátum',
  'form.sector': 'Ágazat',
  'form.countryCode': 'Országkód',
  'form.entityType': 'Szervezettípus',
  'form.region': 'Régió',
  'form.staffCount': 'Létszám',
  'form.borrowers': 'Hitelfelvevők',
  'form.contactName': 'Kapcsolattartó neve',
  'form.contactEmail': 'Kapcsolattartó e-mail',
  'form.templateName': 'Sablon neve',
  'form.templateType': 'Sablon típusa',
  'form.indicators': 'Mutatók',
  'form.reportingPeriod': 'Jelentési időszak',
  'form.reportingYear': 'Jelentési év',
  'form.regulation': 'Szabályozás',
  'form.filingType': 'Beadvány típusa',
  'form.reportType': 'Jelentés típusa',
  'form.emailAddress': 'E-mail cím',
  'form.role': 'Szerepkör',
  'form.selectFund': 'Válasszon alapot...',
  'form.selectInvestee': 'Válasszon vállalatot...',
  'table.code': 'Kód',
  'table.investee': 'Vállalat',
  'table.instrument': 'Eszköz',
  'table.amount': 'Összeg',
  'table.currency': 'Pénznem',
  'table.maturity': 'Lejárat',
  'table.name': 'Név',
  'table.country': 'Ország',
  'table.type': 'Típus',
  'table.period': 'Időszak',
  'table.completion': 'Befejezés',
  'table.submitted': 'Beküldve',
  'table.version': 'Verzió',
  'table.indicators': 'Mutatók',
  'table.dueDate': 'Határidő',
  'table.generated': 'Generálva',
  'table.email': 'E-mail',
  'table.role': 'Szerepkör',
  'error.createFailed': 'Létrehozás sikertelen. Próbálja újra.',
  'error.updateFailed': 'Frissítés sikertelen. Próbálja újra.',
  'error.loadFailed': 'Adatok betöltése sikertelen.',

  // Sidebar Sections
  'sections.portfolio': 'PORTFÓLIÓ',
  'sections.dataCollection': 'ADATGYŰJTÉS',
  'sections.intelligence': 'ELEMZÉS',
  'sections.ecosystem': 'ÖKOSZISZTÉMA',
  'sections.admin': 'ADMINISZTRÁCIÓ',
  'sections.other': 'EGYÉB',

  // Page Titles
  'pages.messagesTitle': 'Üzenetek',
  'pages.taxonomyTitle': 'EU Taxonómia',
  'pages.biStudioTitle': 'BI Studio',
  'pages.knowledgeGraphTitle': 'Tudásgráf',
  'pages.benchmarksTitle': 'Referenciaértékek',
  'pages.mcpTitle': 'MCP integráció',
  'pages.tasksTitle': 'Feladatok',
  'pages.submitDataTitle': 'ESG adatok beküldése',

  // Empty States
  'empty.benchmarksComingSoon': 'Referenciaértékek hamarosan',

  // Labels
  'labels.sfdr': 'SFDR',
  'labels.euTaxonomy': 'EU Taxonómia',
  'labels.csrd': 'CSRD',
  'labels.mifid': 'MiFID',
  'labels.other': 'Egyéb',
  'labels.critical': 'Kritikus',
  'labels.high': 'Magas',
  'labels.medium': 'Közepes',
  'labels.low': 'Alacsony',

  // Count / summary strings
  'count.fundsTotal': '{count} fund(s) total',
  'count.investmentsTotal': '{count} investment(s) total',
  'count.investeesTotal': '{count} investee(s) total',

  // Filter options
  'filter.allStatus': 'All Status',
  'filter.allSfdr': 'All SFDR',

  // Empty states
  'empty.noFundsYet': 'No funds yet. Create your first fund.',
  'empty.noFundsMatch': 'No funds match your filters.',
  'empty.noInvestmentsYet': 'No investments yet.',
  'empty.noInvestmentsMatch': 'No investments match your search.',
  'empty.noInvesteesYet': 'No investees yet.',
  'empty.noInvesteesMatch': 'No investees match your search.',
  'empty.noTemplatesYet': 'No ESG templates configured yet.',
  'empty.noSubmissionsYet': 'No ESG submissions yet.',
  'empty.noComplianceData': 'No SFDR compliance data available.',
  'empty.noFilingsYet': 'No compliance filings yet.',
  'empty.noReportsYet': 'No reports generated yet.',
  'empty.selectFundPai': 'Select a fund to view PAI indicators.',
  'empty.noPaiIndicators': 'No PAI indicators found for this fund.',
  'empty.selectFundCalendar': 'Select a fund to view compliance calendar.',
  'empty.noCalendarEvents': 'No upcoming events for this fund.',

  // Page subtitles
  'subtitle.esg': 'Templates and data submissions',
  'subtitle.compliance': 'SFDR, PAI indicators, and regulatory filings',
  'subtitle.reports': 'Generate and manage compliance reports',
  'subtitle.settings': 'Manage your account and preferences',

  // Tab labels
  'tab.templates': 'Templates',
  'tab.submissions': 'Submissions',
  'tab.sfdr': 'SFDR',
  'tab.paiIndicators': 'PAI Indicators',
  'tab.filings': 'Filings',
  'tab.reports': 'Reports',
  'tab.calendar': 'Calendar',

  // Compliance table headers
  'table.indicator': 'Indicator',
  'table.category': 'Category',
  'table.metric': 'Metric',
  'table.value': 'Value',
  'table.mandatory': 'Mandatory',
  'table.data': 'Data',
  'table.deadline': 'Deadline',
  'table.taxonomy': 'Taxonomy',

  // Yes/No
  'general.yes': 'Yes',
  'general.no': 'No',

  // Error messages
  'error.loadTemplates': 'Failed to load templates.',
  'error.loadSubmissions': 'Failed to load submissions.',
  'error.loadCompliance': 'Failed to load compliance data.',
  'error.loadPai': 'Failed to load PAI indicators.',
  'error.loadFilings': 'Failed to load filings.',
  'error.loadCalendar': 'Failed to load calendar events.',
  'error.createTemplate': 'Failed to create template. Please try again.',
  'error.createSubmission': 'Failed to create submission. Please try again.',
  'error.createFiling': 'Failed to create filing. Please try again.',
  'error.generateReport': 'Failed to generate report.',
  'error.templateNameRequired': 'Template name is required',
  'error.selectFund': 'Select a fund',
  'error.selectInvestee': 'Select an investee',
  'error.validAmount': 'Enter a valid amount',
  'error.reportingPeriodRequired': 'Reporting period is required',
  'error.validYearRequired': 'Valid year is required',
  'error.enterPeriod': 'Enter a period',
  'error.nameRequired': 'Name is required',
  'error.countryCodeRequired': 'Country code is required',
  'error.operationFailed': 'Operation failed.',
  'error.pleaseselectFund': 'Please select a fund',

  // Success messages
  'success.submissionCreated': 'Submission created successfully.',
  'success.profileSaved': 'Profile saved successfully.',

  // Relative time
  'time.justNow': 'Just now',
  'time.minutesAgo': '{count}m ago',
  'time.hoursAgo': '{count}h ago',
  'time.daysAgo': '{count}d ago',

  // Settings
  'settings.profile': 'Profile',
  'settings.organization': 'Organization',
  'settings.apiKeys': 'API Keys',
  'settings.notifications': 'Notifications',
  'settings.orgId': 'Org ID',
  'settings.tier': 'Tier',
  'settings.apiKeysDescription': 'Contact your organization administrator to manage API keys and integration credentials.',
  'settings.apiKeysNote': 'API keys provide programmatic access to Impact Core data. For security, key creation and revocation is restricted to organization admins.',
  'settings.notifReportAlerts': 'Report generation alerts',
  'settings.notifReportAlertsDesc': 'Email when a report is generated or fails',
  'settings.notifComplianceReminders': 'Compliance deadline reminders',
  'settings.notifComplianceRemindersDesc': 'Email reminders for upcoming filing deadlines',
  'settings.notifEsgUpdates': 'ESG submission updates',
  'settings.notifEsgUpdatesDesc': 'Email when investee submissions change status',
  'settings.notifInAppActivity': 'In-app activity feed',
  'settings.notifInAppActivityDesc': 'Show real-time activity notifications in header',

  // Data Completeness label
  'compliance.dataCompletenessLabel': 'Data Completeness',
};
